/**
 * "What changed?" on the project Home (design "A calmer project Home"): one list, five rows at most
 * (utils/homeModel.js whatChangedItems): the unread handoff note pinned first, who is live now, Shadow AI when the
 * project used an AI tool outside the approved list, then the latest activity since you left. The header says since
 * when and how much ("Since you left yesterday at 18:40 · 23 changes in 9 files") with links to the Changes and
 * Activity tabs (`?tab=`). Nothing new: one line, "No changes since … · see History".
 *
 * Data: vfs-service's unseen handoff notes, since-last-seen and activity (services/CollabService.js), this project's
 * entry of the shared `/api/v1/live` poll (hooks/useLiveNow.js); `shadow` comes from the caller (the provenance).
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { dismissHandoff, getActivity, getSinceLastSeen, getUnseenHandoffs } from '../../services/CollabService';
import useLiveNow from '../../hooks/useLiveNow';
import { followPerson } from '../../utils/presenceClient';
import { eventLine, historyTargetOf, linkOf, participantOf } from '../../utils/collab3Model';
import { sinceFor } from '../../utils/changesModel';
import { ago } from '../../utils/blameModel';
import { openHistory } from '../../utils/workspaceModel';
import { usePlanTab } from '../../hooks/usePlanFeatures';
import { leftAt } from '../collab/SinceYouLeft';
import { AuthorMark } from '../collab/ChangesPanel';
import { NoteBody } from '../collab/Handoff';
import { ParticipantMark } from '../presence/Presence';
import Icon from '../icons/Icon';
import { GadgetSkeleton } from './gadgets/GadgetCard';
import { changesInFiles, liveSentence, sinceAuthors, whatChangedItems } from '../../utils/homeModel';
import { activityChips } from '../../utils/activityStrip';
import { firstName } from '../../utils/presenceModel';

const REFRESH_MS = 60000;

/** A link to a workspace tab: a real `?tab=` URL (open in a new tab works), opened in place on a plain click. */
function TabLink({ tab, children, testId }) {
  // Not a link when the person's plan hides the tab: just its text.
  const shows = usePlanTab();
  if (!shows(tab)) { return <span data-testid={testId}>{children}</span>; }
  const onClick = (event) => {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1) { return; }
    event.preventDefault();
    openHistory({ tab });
  };
  return <a className="ph-link" data-testid={testId} href={`?tab=${tab}`} onClick={onClick}>{children}</a>;
}

TabLink.propTypes = { children: PropTypes.node, tab: PropTypes.string.isRequired, testId: PropTypes.string };

function HandoffRow({ item, onRead, onDismiss }) {
  const { note, more } = item;
  return (
    <li className="ph-row ph-row-pinned" data-kind="handoff" data-testid="what-changed-item">
      <ParticipantMark initials={note.by?.initials || '?'} label={note.by?.name} size={28} slot={note.by?.slot ?? 0} />
      <div className="ph-row-text">
        <div className="ph-row-head">
          <strong>{note.by?.name || 'Someone'} {note.forYou ? 'left you a note' : 'left a note'}</strong>
          <span className="ph-chip ph-chip-pin">Pinned · unread</span>
          <span className="ph-muted ph-small">{ago(note.at)}</span>
        </div>
        <NoteBody note={note} />
        {more > 0 && <div className="ph-muted ph-small">{more} more note{more === 1 ? '' : 's'} after this one.</div>}
      </div>
      <div className="ph-row-actions">
        {onRead && <button className="ph-btn ph-btn-small" onClick={() => onRead(note.id)} type="button">Read note</button>}
        <button className="ph-link" data-testid="handoff-dismiss" onClick={onDismiss} type="button">Dismiss</button>
      </div>
    </li>
  );
}

HandoffRow.propTypes = { item: PropTypes.object.isRequired, onDismiss: PropTypes.func.isRequired, onRead: PropTypes.func };

function LiveRow({ item, projectId }) {
  const person = item.follow;
  return (
    <li className="ph-row" data-kind="live" data-testid="what-changed-item">
      <span className="ph-row-marks">
        {item.rows.slice(0, 3).map((row) => (row.kind === 'person' ? (
          <ParticipantMark initials={row.mark.initials || '?'} key={row.key} label={row.mark.name} size={28} slot={row.mark.slot ?? 0} />
        ) : (
          <ParticipantMark agent initials={row.mark.driver?.initials || 'AI'} key={row.key} label={row.text} paused={Boolean(row.mark.paused)} size={28} slot={row.mark.slot ?? 0} />
        )))}
      </span>
      <div className="ph-row-text">
        <span className="ph-chip ph-chip-live"><span aria-hidden="true" className="ph-dot" />Live now</span>
        {' '}{liveSentence(item.rows)}
      </div>
      {person && (
        <button className="ph-btn ph-btn-small" onClick={() => followPerson(projectId, person.id)} type="button">Follow {firstName(person.name)}</button>
      )}
    </li>
  );
}

LiveRow.propTypes = { item: PropTypes.object.isRequired, projectId: PropTypes.string.isRequired };

function ShadowRow({ item, onOpenAi }) {
  const { shadow } = item;
  const tools = (shadow.reasons || []).map((reason) => reason.label || reason.value).filter(Boolean);
  return (
    <li className="ph-row ph-row-warn" data-kind="shadow" data-testid="what-changed-item">
      <span className="ph-row-icon"><Icon glow={false} name="warning" size={16} /></span>
      <div className="ph-row-text">
        <strong>Shadow AI</strong>
        {' '}{tools.length > 0 ? `${tools.slice(0, 2).join(', ')} ${tools.length === 1 ? 'is' : 'are'} not on the approved AI list` : 'An AI tool outside the approved list changed this project'}
        {shadow.explanation && <div className="ph-muted ph-small">{shadow.explanation}</div>}
      </div>
      {onOpenAi && <button className="ph-link" onClick={onOpenAi} type="button">See AI used</button>}
    </li>
  );
}

ShadowRow.propTypes = { item: PropTypes.object.isRequired, onOpenAi: PropTypes.func };

function EventRow({ item, onOpenSuggestion }) {
  const { event } = item;
  const who = participantOf(event);
  const link = linkOf(event);
  const review = link?.to === 'suggestion' && onOpenSuggestion ? link.suggestionId : null;
  return (
    <li className="ph-row" data-kind="event" data-testid="what-changed-item" data-type={event.type}>
      <button className="ph-row-main" onClick={() => openHistory(historyTargetOf(event))} type="button">
        {who ? <AuthorMark author={who.author} size={28} via={who.kind === 'agent' ? who.via || 'Flow' : null} /> : <span className="ph-row-icon" />}
        <span className="ph-row-text">{eventLine(event)}</span>
      </button>
      {review && <button className="ph-link" onClick={() => onOpenSuggestion(review)} type="button">Review</button>}
      <time className="ph-muted ph-small" dateTime={event.at}>{ago(event.at)}</time>
    </li>
  );
}

EventRow.propTypes = { item: PropTypes.object.isRequired, onOpenSuggestion: PropTypes.func };

export default function WhatChanged({ projectId, you = null, shadow = null, onOpenHandoff = null, onOpenSuggestion = null, onOpenAi = null, refreshKey = 0 }) {
  const [notes, setNotes] = useState([]);
  const [since, setSince] = useState(null); // the since-last-seen answer
  const [events, setEvents] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const liveAnswer = useLiveNow();
  const live = (liveAnswer?.projects || []).find((project) => project.projectId === projectId) || null;

  const load = useCallback(async() => {
    const [handoffs, sinceAnswer, activity] = await Promise.allSettled([
      getUnseenHandoffs(projectId),
      getSinceLastSeen(projectId),
      getActivity(projectId, { since: sinceFor('7d'), limit: 30 }),
    ]);
    if (handoffs.status === 'fulfilled') { setNotes(handoffs.value?.notes || []); }
    if (sinceAnswer.status === 'fulfilled') { setSince(sinceAnswer.value || null); }
    if (activity.status === 'fulfilled') { setEvents(activity.value?.events || []); }
    setLoaded(true);
  }, [projectId]);

  useEffect(() => {
    setNotes([]);
    setSince(null);
    setEvents([]);
    setLoaded(false);
    load();
    const timer = setInterval(() => { if (document.visibilityState === 'visible') { load(); } }, REFRESH_MS);
    return () => clearInterval(timer);
  }, [load]);
  // The Home refreshed (its own live refresh, or its Refresh button): asked again, what is shown stays meanwhile.
  const firstKey = useRef(refreshKey);
  useEffect(() => { if (refreshKey !== firstKey.current) { load(); } }, [refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // The first answer: the card's frame with a placeholder, so the dashboard keeps its shape while it loads.
  if (!loaded) {
    return (
      <section aria-label="What changed" className="ph-card ph-changed" data-testid="what-changed-loading">
        <div className="ph-card-head"><h2>What changed</h2></div>
        <GadgetSkeleton label="Reading what changed…" testId="what-changed-skeleton" />
      </section>
    );
  }

  const [note] = notes;
  const items = whatChangedItems({ handoff: note || null, moreNotes: Math.max(0, notes.length - 1), live, shadow, events, since: since?.since || null, you });
  const dismiss = () => {
    setNotes((current) => current.slice(1));
    dismissHandoff(projectId, note.id).catch(() => {});
  };
  const leftWhen = since?.since ? leftAt(since.since) : '';

  if (items.length === 0) {
    // Nothing new, but the latest of each kind (edit, deploy, share…) in one small row, each opening its details.
    const chips = activityChips(events);
    return (
      <section aria-label="What changed" className="ph-card ph-quiet" data-testid="what-changed-quiet">
        <span>{leftWhen ? `Nothing new since you last looked, ${leftWhen.replace(/^on /, '')}` : 'No changes in the last 7 days'}</span>
        {chips.length > 0 && (
          <span aria-label="Latest activity" className="ph-activity" data-testid="what-changed-latest" role="group">
            <span className="ph-muted ph-small">Latest</span>
            {chips.map((chip) => (
              <button className="ph-activity-chip" data-type={chip.event.type} key={chip.key} onClick={() => openHistory(historyTargetOf(chip.event))} title={eventLine(chip.event)} type="button">
                {chip.label}
                <time className="ph-muted" dateTime={chip.at}>{ago(chip.at)}</time>
              </button>
            ))}
          </span>
        )}
        <span aria-hidden="true" className="ph-muted">·</span>
        <TabLink tab="changes" testId="what-changed-history">see History</TabLink>
      </section>
    );
  }

  const changes = since?.changes || 0;
  const summary = changesInFiles(since);
  const authors = changes ? sinceAuthors(since, you) : '';
  return (
    <section aria-labelledby="ph-what-changed" className="ph-card ph-changed" data-testid="what-changed">
      <div className="ph-card-head">
        <h2 id="ph-what-changed">What changed</h2>
        {leftWhen && <span className="ph-muted ph-small">Since you left {leftWhen}{summary ? ` · ${summary}` : ''}</span>}
        <span className="ph-spacer" />
        <span className="ph-card-links">
          <TabLink tab="changes" testId="what-changed-changes">{changes ? `Changes (${changes})` : 'Changes'}</TabLink>
          <TabLink tab="activity" testId="what-changed-activity">Activity</TabLink>
        </span>
      </div>
      <ul className="ph-rows">
        {items.map((item) => {
          if (item.kind === 'handoff') { return <HandoffRow item={item} key={item.key} onDismiss={dismiss} onRead={onOpenHandoff} />; }
          if (item.kind === 'live') { return <LiveRow item={item} key={item.key} projectId={projectId} />; }
          if (item.kind === 'shadow') { return <ShadowRow item={item} key={item.key} onOpenAi={onOpenAi} />; }
          return <EventRow item={item} key={item.key} onOpenSuggestion={onOpenSuggestion} />;
        })}
      </ul>
      {changes > 0 && (
        <div className="ph-card-foot ph-muted ph-small">
          <span>{authors}</span>
          <span className="ph-spacer" />
          <TabLink tab="changes">See all {changes} in Changes</TabLink>
        </div>
      )}
    </section>
  );
}

WhatChanged.propTypes = {
  onOpenAi: PropTypes.func,
  /** Bumped when the Home refreshes: asked again. */
  refreshKey: PropTypes.number,
  onOpenHandoff: PropTypes.func,
  onOpenSuggestion: PropTypes.func,
  projectId: PropTypes.string.isRequired,
  shadow: PropTypes.object,
  you: PropTypes.object,
};
